import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import { ProjectSupplierApprovalDtoIn } from '@/models/in/ProjectSupplierApprovalDtoIn';
import { SupplierApprovalStatus } from '@/models/common/ProjectStatus';
import { UpdateSupplierApprovalStatusDtoOut } from '@/models/out/ProjectDtoOut';
import { getSupplierApprovalStatusColor } from '@/helpers/projectStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { putAuth } from '@/api/ApiCalls';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';

const BASE_URL = 'management/projects';

const linkKey = (row: Pick<ProjectSupplierApprovalDtoIn, 'projectId' | 'supplierId'>) =>
  `${row.projectId}-${row.supplierId}`;

const linkSearch: FilterFn<ProjectSupplierApprovalDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const { supplierName, taxId, activityDomainName, activityDomainOther, projectName, fundId, fundName } = row.original;
  return [supplierName, taxId, activityDomainName, activityDomainOther, projectName, String(fundId), fundName].some(
    (field) => field?.toLowerCase().includes(searchValue)
  );
};

/**
 * The admin "אישור ספקים" queue — the other half of the split-on-submit flow.
 *
 * SPR-10302: a supplier is approved for a specific project, not globally ("כל ספק מאושר רק למיזם
 * מסויים ... רק לאחר שנפתחה מיזם וצירפו אותו למיזם ניתן לאשר או לדחות"). So every row here is a
 * supplier on one project, and approving or rejecting it decides that project only.
 */
const SuppliersApprovalPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [selectedTab, setSelectedTab] = useState<SupplierApprovalStatus>(SupplierApprovalStatus.PendingApproval);
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingLinkKey, setUpdatingLinkKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: links, isLoading, refetch } = useGetAuthApi<ProjectSupplierApprovalDtoIn[]>(
    `${BASE_URL}/supplier-links?approvalStatus=${selectedTab}`
  );

  const handleUpdateStatus = async (link: ProjectSupplierApprovalDtoIn, approvalStatus: SupplierApprovalStatus) => {
    setError(null);
    setUpdatingLinkKey(linkKey(link));
    try {
      await putAuth<ProjectSupplierApprovalDtoIn, object, UpdateSupplierApprovalStatusDtoOut>(
        `${BASE_URL}/${link.projectId}/suppliers/${link.supplierId}/approval-status`, headers, {}, { approvalStatus }
      );
      refetch();
    } catch (err) {
      console.error('Error updating supplier approval status:', err);
      setError(t('suppliersApproval.errorUpdating'));
    } finally {
      setUpdatingLinkKey(null);
    }
  };

  const columns: ColumnDef<ProjectSupplierApprovalDtoIn>[] = [
    {
      id: 'supplierName',
      header: Col(t('suppliersApproval.columns.name')),
      accessorKey: 'supplierName',
      enableSorting: true,
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'taxId',
      header: Col(t('suppliersApproval.columns.taxId')),
      accessorKey: 'taxId',
      enableSorting: true,
      meta: { className: 'min-w-[130px]' },
    },
    {
      id: 'activityDomain',
      header: Col(t('suppliersApproval.columns.activityDomain')),
      enableSorting: false,
      // A supplier carries either a closed-list domain or the free-text "אחר" value.
      cell: ({ row }) => (
        <span>{row.original.activityDomainName ?? row.original.activityDomainOther ?? ''}</span>
      ),
      meta: { className: 'min-w-[150px]' },
    },
    {
      // The project the office approves the supplier for.
      id: 'projectName',
      header: Col(t('suppliersApproval.columns.project')),
      accessorKey: 'projectName',
      enableSorting: true,
      meta: { className: 'min-w-[180px]' },
    },
    {
      // The office works by fund number, as on the projects screen.
      id: 'fundId',
      header: Col(t('projectsManagement.columns.fundNumber')),
      accessorKey: 'fundId',
      enableSorting: true,
      meta: { className: 'w-[100px] min-w-[100px] max-w-[100px]' },
    },
    {
      id: 'fundName',
      header: Col(t('projectsManagement.columns.fund')),
      accessorKey: 'fundName',
      enableSorting: true,
      meta: { className: 'min-w-[160px]' },
    },
    {
      id: 'approvalStatus',
      header: Col(t('suppliersApproval.columns.approvalStatus')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div
            className="w-[10px] h-[10px] rounded-full shrink-0"
            style={{ backgroundColor: getSupplierApprovalStatusColor(row.original.approvalStatus) }}
          />
          <span>{t(`suppliersApproval.approvalStatus.${row.original.approvalStatus}`)}</span>
        </div>
      ),
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'createdAt',
      header: Col(t('suppliersApproval.columns.linkedAt')),
      accessorKey: 'createdAt',
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: { className: 'min-w-[130px] max-w-[130px]' },
    },
    {
      id: 'actions',
      header: Col(''),
      enableSorting: false,
      cell: ({ row }) => {
        const isUpdating = updatingLinkKey === linkKey(row.original);
        return (
          <div className="flex gap-2">
            {row.original.approvalStatus !== SupplierApprovalStatus.Approved && (
              <Button
                variant="outline"
                disabled={isUpdating}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateStatus(row.original, SupplierApprovalStatus.Approved);
                }}
              >
                {t('suppliersApproval.approve')}
              </Button>
            )}
            {row.original.approvalStatus !== SupplierApprovalStatus.Rejected && (
              <Button
                variant="outline"
                className="!text-red-500 !border-red-300"
                disabled={isUpdating}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateStatus(row.original, SupplierApprovalStatus.Rejected);
                }}
              >
                {t('suppliersApproval.reject')}
              </Button>
            )}
          </div>
        );
      },
      meta: { className: 'min-w-[190px]' },
    },
  ];

  return (
    <div>
      <Tabs
        value={selectedTab}
        onValueChange={(value) => setSelectedTab(value as SupplierApprovalStatus)}
        style={{ direction: 'rtl' }}
        className="mb-6"
      >
        <TabsList>
          <TabsTrigger value={SupplierApprovalStatus.PendingApproval}>{t('suppliersApproval.tabs.pending')}</TabsTrigger>
          <TabsTrigger value={SupplierApprovalStatus.Approved}>{t('suppliersApproval.tabs.approved')}</TabsTrigger>
          <TabsTrigger value={SupplierApprovalStatus.Rejected}>{t('suppliersApproval.tabs.rejected')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {error && <p className="text-red-500 body-sm-regular mb-3">{error}</p>}

      <DataTable
        title={t('suppliersApproval.title')}
        data={links ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-end gap-3">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="suppliersApproval.search"
            />
          </div>
        }
        emptyStateContent={{ title: t('suppliersApproval.noData'), description: t('suppliersApproval.noDataDescription') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={linkSearch}
      />
    </div>
  );
};

export default SuppliersApprovalPage;
